<template>
    <DsModal
        :is-open="isOpen"
        class="bundle-install-modal"
    >
        <template #heading>
            <DsModalHeader sticky>
                <template #leading>
                    <DsIconButton
                        name="x"
                        data-qa="close"
                        @click="close"
                    />
                </template>

                <template #title>
                    <DsModalHeaderTitle>
                        <template #title>
                            <span data-qa="title">{{ modalTitle }}</span>
                        </template>
                    </DsModalHeaderTitle>
                </template>

                <template v-if="!installationCompleted" #trailing>
                    <DsButton
                        :disabled="actionDisabled"
                        :loading="installing"
                        class="install-button"
                        data-qa="next"
                        dense
                        @click="installBundles"
                    >
                        {{ actionText }}
                    </DsButton>
                </template>
            </DsModalHeader>
        </template>

        <template #default>
            <div class="bundle-install">
                <section class="semibold">
                    {{ $t('campaignsPage.bundles.installModal.campaignsSection') }}
                </section>

                <section>
                    <div class="campaigns">
                        <div v-if="loadingListings" class="campaigns-placeholder">
                            <DsPlaceholder
                                v-for="n in bundle.solutions.length"
                                :key="n"
                                class="placeholder"
                                :rows="placeholderRows"
                            />
                        </div>

                        <p
                            v-for="{ name, id, archived } in bundle.solutions"
                            v-else
                            :key="id"
                        >
                            <DsIcon name="check" class="icon" />
                            {{ name }}
                            <span v-if="archived" class="archived-pill">
                                <pill
                                    :label="$t('campaignsPage.bundles.installModal.campaignArchived')"
                                    readonly
                                />
                            </span>
                        </p>
                    </div>
                </section>

                <section class="accounts-container">
                    <div class="accounts">
                        <div class="accounts-header">
                            <div class="title">
                                <span class="semibold"> {{ $t('campaignsPage.bundles.installModal.accounts') }} </span>
                                <span class="showing" v-text="filteredResults.length + ' of ' + accounts.length" />
                            </div>
                        </div>

                        <div v-if="!installing" class="select-all-and-search">
                            <DsCheckbox
                                v-model="selectAll"
                                data-qa="selectAllCheckbox"
                                :partial="partialSelectedAccounts"
                                @input="selectAllAccounts"
                            >
                                <span :class="{semibold: selectedAccounts.length > 0}">
                                    {{ selectAllText }}
                                </span>
                            </DsCheckbox>

                            <div class="search-container">
                                <DsIcon class="search-icon" name="search" />
                                <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
                                <input
                                    ref="input"
                                    v-model="searchText"
                                    type="text"
                                    name="company"
                                    class="search-input"
                                    placeholder="Filter"
                                />
                            </div>
                        </div>

                        <div :class="['account-list', { installing }]">
                            <div
                                v-for="(account, i) in flagshipUserAccounts"
                                :key="i"
                                class="account-container"
                            >
                                <div
                                    :class="['account', {
                                        hidden: !filteredResults.includes(account),
                                        compatible: account.status === compatible,
                                        incompatible: account.status === incompatible,
                                        selected: account.selected,

                                    }]"
                                    :data-qa="account.account.tenantId"
                                >
                                    <DsCheckbox
                                        v-model="account.selected"
                                        :disabled="account.status === incompatible || loading || installing || installationCompleted"
                                        @input="selectAccount(account)"
                                    />

                                    <div class="account-info">
                                        <p>{{ account.account.companyProfile.name }}</p>
                                        <p>{{ account.account.tenantId }}</p>
                                    </div>

                                    <div class="progress">
                                        <div v-if="installing">
                                            <span class="progress-status">
                                                <span v-if="bundle.solutions.length === account.installSummary.failed.length" class="failed">
                                                    {{ $t('campaignsPage.bundles.installModal.failed') }}
                                                    <DsIcon name="x-circle" />
                                                </span>

                                                <span v-else-if="bundle.solutions.length === account.installSummary.completed.length">
                                                    {{ $t('campaignsPage.bundles.installModal.installed') }}
                                                    <DsIcon name="check" />
                                                </span>

                                                <span v-else-if="account.installSummary.failed.length > 0" class="partial-success">
                                                    {{ $t('campaignsPage.bundles.installModal.partial') }}
                                                    <DsIcon name="alert-circle" />
                                                </span>

                                                <div v-else id="progress-bar-container">
                                                    <div
                                                        id="loaded-progress-bar"
                                                        ref="loadedProgressBar"
                                                        :class="{'error': account.installSummary.error.length > 0}"
                                                    />
                                                </div>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="account.installSummary && account.installSummary.failed"
                                >
                                    <div
                                        v-for="campaign in account.installSummary.failed"
                                        :key="campaign.campaignId"
                                        class="error-message-container"
                                    >
                                        <p>{{ campaign.listingEditionTitle }}</p>
                                        <ul>
                                            <li
                                                v-for="error in campaign.resultDetails"
                                                :key="error"
                                            >
                                                {{ error }}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <template v-if="filteredResults.length === 0">
                            <div v-if="searchText" class="empty-state-box">
                                <h3 class="semibold">
                                    {{ $t('campaignsPage.bundles.installModal.emptyStateFiltered.title') }}
                                </h3>
                                <p v-html="$t('campaignsPage.bundles.installModal.emptyStateFiltered.body')" />
                                <img src="@/assets/images/empty-state.png" />
                            </div>
                            <div v-else class="empty-state-box">
                                <h3 class="semibold">
                                    {{ $t('campaignsPage.bundles.installModal.emptyState.title') }}
                                </h3>
                                <i18n path="campaignsPage.bundles.installModal.emptyState.body" tag="p">
                                    <a place="anchor" :href="accountSignupUrl">
                                        {{ $t('campaignsPage.bundles.installModal.emptyState.urlText') }}
                                    </a>
                                </i18n>
                                <img src="@/assets/images/empty-state.png" />
                            </div>
                        </template>
                    </div>
                </section>
            </div>
        </template>
    </DsModal>
</template>

<script>
import Rx from 'rxjs/Rx';

import { mapGetters } from 'vuex';
import analyticsHelper from '@/mixins/analytics';

export default {
    props: {
        bundle: {
            type: Object,
            default: () => { return { solutions: [] }; },
        },
        title: {
            type: String,
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            loading: false,
            installing: false,
            installationComplete: false,
            compatible: 'Created',
            incompatible: 'Failed',
            loadingListings: true,
            activeAccount: null,
            selectedAccounts: [],
            searchText: '',
            placeholderRows: [{ height: '.75rem', boxes: [1] }],
            accounts: [],
            validationsCompleted: 0,
            selectAll: false,
            accountSignupUrl: `${process.env.VUE_APP_CAS_BASE_URL}/registration/createInfusionsoftId`,
        };
    },

    watch: {
        isOpen(val) {
            if (val) {
                this.open();
            }
        },
    },

    computed: {
        ...mapGetters({
            flagshipUserAccounts: 'auth/flagshipUserAccounts',
        }),

        modalTitle() {
            return this.title || this.$t('campaignsPage.bundles.installModal.titleBundle');
        },

        filteredResults() {
            if (!this.flagshipUserAccounts) {
                return null;
            }

            if (!this.searchText) {
                return this.flagshipUserAccounts;
            }

            const searchString = this.searchText.toLowerCase();

            return this.flagshipUserAccounts.filter(({ account: { tenantId = '', companyProfile: { name = '' } = {} } = {} }) => {
                return name.toLowerCase().includes(searchString)
                    || tenantId.toLowerCase().includes(searchString);
            });
        },

        actionText() {
            const actionText = `${this.installing ? 'actionInstalling' : 'action'}`;

            return this.$t(`campaignsPage.bundles.installModal.${actionText}`);
        },

        actionDisabled() {
            return !this.selectedAccounts.length || this.installing;
        },

        installationCompleted() {
            const accountsCompleted = this.selectedAccounts.filter(({ installSummary }) => {
                return installSummary.completed.length + installSummary.failed.length === this.bundle.solutions.length;
            });

            return accountsCompleted.length === this.selectedAccounts.length && this.installing || this.installationComplete;
        },

        selectAllText() {
            if (this.selectedAccounts.length > 0) {
                return `${this.selectedAccounts.length} selected`;
            }

            return this.$t('Select all');
        },

        partialSelectedAccounts() {
            return (this.selectedAccounts.length > 0 && this.selectedAccounts.length < this.accounts.length);
        },
    },

    methods: {
        getCurrentUserAccounts() {
            this.$store.dispatch('auth/GET_CURRENT_USER_ACCOUNTS')
                .then(this.formatAccountList)
                .catch(() => {
                    this.$error({
                        message: this.$t('notifications.error.bundlesPage.userAccounts'),
                    });
                });
        },

        formatAccountList() {
            this.accounts = [...this.flagshipUserAccounts];
            this.accounts.forEach((account) => {
                account.selected = false;
                account.status = null;
                account.installSummary = {
                    completed: [],
                    failed: [],
                    error: [],
                };
            });
        },

        getBundleListings() {
            if (this.bundle.solutions.length > 0 && this.bundle.solutions[0].immutableId) {
                // already got the details, so skip getting again
                this.loadingListings = false;

                return;
            }

            const listings = this.bundle.solutions.map((listing) => listing.id);

            this.$store.dispatch('automations/GET_BUNDLE_LISTINGS', listings)
                .then(({ content }) => {
                    // TODO: fix and remove this override (it was grandfathered in)
                    // eslint-disable-next-line vue/no-mutating-props
                    this.bundle.solutions = content;
                    this.loadingListings = false;
                })
                .catch(() => {
                    this.$error({
                        message: this.$t('notifications.error.bundlesPage.getBundleListings'),
                    });
                });
        },

        selectAllAccounts() {
            if (this.selectAll) {
                this.filteredResults.forEach((account) => {
                    if (!account.selected) {
                        account.selected = true;
                        this.attemptToSelectAccount(account);
                    }
                });
            } else {
                this.filteredResults.forEach((account) => {
                    account.selected = false;
                    this.attemptToSelectAccount(account);
                });
            }
        },

        selectAccount(account) {
            this.checkSelectAll();
            this.attemptToSelectAccount(account);
        },

        checkSelectAll() {
            this.selectAll = true;
        },

        attemptToSelectAccount(account) {
            account.status = this.compatible;

            if (account.selected) {
                this.activeAccount = account;
                this.loading = true;

                this.addAccount(account);
                this.resetLoadingStatus();
            } else {
                this.removeAccount(account);

                if (this.selectedAccounts.length === 0) { this.selectAll = false; }
                account.selected = false;
            }
        },

        resetLoadingStatus() {
            this.validationsCompleted = 0;
            this.loading = false;
        },

        async open() {
            this.selectAll = false;
            this.formatAccountList();
            await Promise.all([
                this.getBundleListings(),
                this.getCurrentUserAccounts(),
            ]);
            this.installing = false;
            this.installationComplete = false;
            this.searchText = '';
            this.selectedAccounts = [];
        },

        close() {
            this.loadingListings = true;
            this.$emit('close');
        },

        installCampaignsForAccount(account) {
            return Rx.Observable.from(this.bundle.solutions)
                .flatMap((campaign) => {
                    return Rx.Observable.fromPromise(this.$store.dispatch('automations/INSTALL_CAMPAIGN', {
                        account,
                        campaign,
                    }))
                        .catch(() => {
                            return Rx.Observable.of({
                                id: campaign.id,
                                appDomain: account.tenantId,
                                status: 'Error',
                                message: this.$t('notifications.error.bundlesPage.installCampaign'),
                            });
                        });
                });
        },

        checkCampaignInstallStatus(id, appDomain, status) {
            return (status !== 'Failed' && status !== 'Completed' && status !== 'Error')
                ? Rx.Observable
                    .fromPromise(this.$store.dispatch('automations/CHECK_INSTALL_STATUS', id))
                    .catch(() => {
                        return Rx.Observable.of({
                            id,
                            appDomain,
                            status: 'Error',
                            message: this.$t('notifications.error.bundlesPage.installStatus'),
                        });
                    })
                    .delay(1500)
                : Rx.Observable.empty();
        },

        installCampaigns() {
            if (this.selectedAccounts.length >= 5) {
                this.close();
                this.$emit('campaigns-installing');
            }
            const { selectedAccounts, bundle } = this;
            let successfulInstall = true;

            Rx.Observable.from(selectedAccounts)
                .map(({ account }) => account)
                .flatMap((account) => this.installCampaignsForAccount(account))
                .expand(({
                    id, appDomain, status,
                }) => this.checkCampaignInstallStatus(id, appDomain, status))
                .subscribe(({
                    id, appDomain, listingEditionTitle, status, currentStepCount, totalStepCount, message, resultDetails,
                }) => {
                    this.processInstallStatus({
                        id, appDomain, status, currentStepCount, totalStepCount, message, resultDetails, listingEditionTitle,
                    });
                }, () => {
                    this.$error({ message: this.$t('notifications.error.bundlesPage.installCampaign') });
                    this.installationComplete = true;
                    successfulInstall = false;
                }, () => {
                    this.installationComplete = true;
                    this.state = 'COMPLETE';

                    if (successfulInstall) { this.segmentCalls(bundle, selectedAccounts); }
                });
        },

        segmentCalls(bundle, selectedAccounts) {
            const bundleProperties = {
                bundleId: bundle.id,
                bundleName: bundle.name,
            };

            if (bundle.id) {
                analyticsHelper.trackEvent({
                    category: 'Interaction',
                    subject: 'Bundle',
                    verb: 'Installed',
                    eventProps: bundleProperties,
                });
            }

            bundle.solutions.forEach((listing) => {
                selectedAccounts.forEach(() => {
                    const listingProperties = {
                        listingId: listing.immutableId,
                        listingName: listing.name,
                        listingSlug: listing.id,
                        listingType: listing.listingType,
                        visibility: (listing.privateListing ? 'private' : 'public'),
                        companyId: listing.publisherCompany.immutableId,
                        companyName: listing.publisherCompany.name,
                    };

                    analyticsHelper.trackEvent({
                        category: 'Interaction',
                        subject: 'Campaign',
                        verb: 'Installed',
                        eventProps: { listingProperties, bundleProperties },
                    });
                });
            });
        },

        installBundles() {
            this.state = 'INSTALLING';
            this.installing = true;
            this.installationComplete = false;
            this.installCampaigns();
        },

        processInstallStatus({
            id, appDomain, status, current, total, message, resultDetails, listingEditionTitle,
        }) {
            const appName = appDomain.split('.')[0];

            if (total > 0) {
                const percentage = (current / total) || 0.0;

                this.setProgress(appName, percentage * 100);
            }

            if (status === 'Completed' || status === 'Failed' || status === 'Error') {
                this.updateInstallSummary({
                    appName, id, status: status.toLowerCase(), message, resultDetails, listingEditionTitle,
                });
            }
        },

        updateInstallSummary({
            appName, campaignId, status, message, resultDetails, listingEditionTitle,
        }) {
            const accountIndex = this.selectedAccounts.findIndex(({ account }) => {
                return account.tenantId === appName;
            });

            this.selectedAccounts[accountIndex].installSummary[status].push({
                listingEditionTitle, appName, campaignId, resultDetails,
            });

            if (status === 'error') {
                this.$error({ message });
            }

            this.$forceUpdate();
        },

        addAccount(account) {
            this.selectedAccounts.push(account);
        },

        removeAccount(account) {
            this.selectedAccounts.splice(this.selectedAccounts.indexOf(account), 1);
        },

        setProgress(appName, percent) {
            if (this.selectedAccounts.length === 0) return;

            const accountIndex = this.accounts.findIndex(({ account }) => {
                return account.tenantId === appName;
            });
            const selectedAccountIndex = this.selectedAccounts.findIndex(({ account }) => {
                return account.tenantId === appName;
            });

            const count = this.bundle.solutions.length;
            const completed = this.selectedAccounts[selectedAccountIndex].installSummary.completed.length || 0;
            const offset = completed / count * 100;
            const progress = offset + (percent / count);

            this.$refs.loadedProgressBar[accountIndex].style.right = (`${100 - progress}%`);
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
    @import "~@/styles/animations";

    .bundle-install-modal {
        --modal-overflow: hidden;
    }

    .install-button {
        margin-right: $spacing-100;
    }

    .showing {
        color: $color-gray-light;
        margin-left: $spacing-200;
    }

    section {
        display: flex;
        padding: $spacing-200 $spacing-300;

        &:last-child {
            border-radius: 0 0 $border-radius $border-radius;
            min-height: px-to-rem(360px);

            @media($small) {
                padding: $spacing-100;
            }
        }
    }

    .accounts-container {
        padding-bottom: 0;
    }

    .campaigns p {
        margin-bottom: $spacing-100;
        display: flex;
        align-items: center;

        .archived-pill {
            display: flex;
            align-items: center;
            --pill-background: #{$color-primary-lightest};
            --pill-label-color: #{$color-gray-darkest};
            margin-left: $spacing-100;

            .pill {
                margin: 0;
            }
        }
    }

    .accounts {
        flex: 1;
        flex-direction: column;
    }

    .accounts-header {
        display: flex;
        margin-bottom: $spacing-200;

        .title {
            flex: 1;
        }

        .input-field {
            margin-right: auto;
            max-width: px-to-rem(300px);
        }
    }

    .input-field {
        max-width: px-to-rem(260px);
    }

    .account-list {
        max-height: px-to-rem(310px);
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;

        @media($small) {
            margin: 0;
            padding: $spacing-200;
        }

        &.installing {
            .account:not(.compatible) {
                display: none;
            }

            .account:not(.selected) {
                display: none;
            }
        }
    }

    .account {
        display: flex;
        flex-direction: row;
        align-items: center;
        margin-bottom: $spacing-200;

        &.incompatible {
            cursor: not-allowed;
            opacity: 0.5;

            .progress-status {
                color: $color-red;
            }
        }

        &.compatible {
            .progress-status {
                color: $color-green;
            }
        }

        &.hidden {
            display: none;
        }

        .progress {
            margin-right: $spacing-200;
        }
    }

    .checkbox {
        position: relative;
        --checkbox-padding: #{$spacing-200} #{$spacing-100} #{$spacing-200} 0;

        @media($small) {
            --checkbox-padding: 0;
        }
    }

    .icon {
        --icon-color: #{$color-gray-light};
        --icon-size: #{px-to-rem(20px)};
        margin-left: $spacing-100;
        margin-right: $spacing-100;

        &.animate {
            color: $color-gray-80;
            @include rotating;
        }
    }

    .progress-status {
        display: flex;
        align-items: center;
        font-size: px-to-rem(12px);

        span {
            display: flex;
            align-items: center;
        }

        .failed {
            color: $color-red;
        }

        .partial-success {
            color: $color-orange;
        }
    }

    .campaigns-placeholder {
        display: flex;
        flex-direction: column;
        width: px-to-rem(200px);

        .placeholder {
            margin-bottom: $spacing-200;
        }
    }

    .account-container {
        flex-direction: column;
    }

    .account-logo {
        margin-right: $spacing-200;
        max-width: px-to-rem(50px);
        max-height: px-to-rem(50px);
        border-radius: $border-radius;

        @media($small) {
            margin-right: $spacing-100;
        }
    }

    .account-info {
        display: flex;
        flex-direction: column;
        margin-right: auto;

        @media($small) {
            padding-right: $spacing-100;
        }

        p {
            margin: 0;
            max-width: px-to-rem(280px);
            overflow: hidden;
            text-overflow: ellipsis;

            @media($small) {
                max-width: px-to-rem(140px);
            }
        }
    }

    .accounts-selected {
        display: flex;
        align-items: center;
    }

    .account-count {
        text-align: center;
        min-width: px-to-rem(24px);
        border-radius: $border-radius;
        background: $color-primary;
        color: $color-white;
        margin-right: $spacing-100;
    }

    #progress-bar-container {
        background-color: $color-gray-30;
        height: 2px;
        width: 70px;
        position: relative;
    }

    #loaded-progress-bar {
        @include transition(all);
        background-color: $color-primary;
        position: absolute;
        top: 0; right: 100%; bottom: 0; left: 0;
        z-index: 1;
    }

    #loaded-progress-bar.error {
        background-color: $color-red;
    }

    .error-message-container{
        padding-bottom: $spacing-200;

        p {
            margin-bottom: 0;
        }

        li {
            padding-left: $spacing-200;
            color: $color-red;
        }
    }

    .search-container {
        display: flex;
        align-items: center;
        background-color: $color-white;
        border-radius: $border-radius;
        color: $color-gray-80;
        padding: $spacing-200 $spacing-200 $spacing-200 0;
        width: 45%;
        max-height: $spacing-400;
        border: 1px solid $color-gray-light;
    }

    .search-input {
        border: 0;
        background-color: transparent;
        color: $color-gray-80;
        font-size: $font-size-sm;
        padding-bottom: $spacing-025;
        width: 100%;
    }

    .select-all-and-search {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid $color-gray-lighter;
        margin: 0 (-$spacing-800) $spacing-200;
        padding: 0 $spacing-800;
    }

    .empty-state-box {
        padding: $spacing-400;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        color: $color-gray-darker;

        @media ($small) {
            width: 100%;
            display: flex;
            flex-direction: column;
        }

        img {
            width: px-to-rem(200px);
        }

        h3 {
            margin-bottom: $spacing-200;
        }

        p {
            padding-bottom: $spacing-400;
            font-size: px-to-rem(16px);
            text-align: center;
        }
    }

    [dir=rtl] {
        .title {
            margin-right: 0;
            margin-left: $spacing-600;
        }

        .accounts-header {
            .input-field {
                margin-right: 0;
                margin-left: auto;
            }
        }

        .account-list {
            padding-right: calc($spacing-200 * 7);
            padding-left: $spacing-200;
        }

        .icon {
            margin-left: 0;
            margin-right: $spacing-100;
        }

        .account {
            img {
                margin-left: $spacing-200;
                margin-right: 0;
            }

            .account-info {
                margin-left: auto;
                margin-right: 0;
            }
        }
    }
</style>
